/**
 * ArchLang Lexer (v0.2)
 *
 * Hand-written lexer for better error messages and IDE integration.
 *
 * Every token carries its surrounding trivia (whitespace and comments),
 * enabling a lossless CST that the formatter can round-trip. Trivia is
 * split between tokens using Roslyn-style rules: trailing trivia runs up
 * to and including the first newline after the token; everything after
 * that newline attaches as leading trivia to the next token (or to EOF
 * if there is no next token).
 *
 * Reserved keywords:
 *   type, in,
 *   aspects, required, cascade, append,
 *   override, drop,
 *   process, subprocess, view,
 *   if, else, select, one, each, try, catch,
 *   parallel, branch, join, race, out, await, reversible, unwind, on,
 *   do, go, as, dist, fail, finish,
 *   focus, group, by, layout, include, exclude,
 *   true, false,
 *   use, from, export
 *
 * v0.8 process language: `switch` was replaced by `select`; `branch`,
 * `join`, `race`, `out`, `await`, `reversible`, `unwind`, `on`, `go`, `as`,
 * `dist`, `one` were added. `;` (SEMICOLON) separates inline steps; `|`
 * (PIPE) is the fan prefix and `\` (BACKSLASH) the snake prefix (§4.13.4).
 *
 * Module/surface/interface base kinds (`module`, `surface`, `interface`) are
 * NOT reserved — they are tokenized as identifiers and resolved by the
 * parser via the kind registry. Stdlib kinds (`service`, `database`,
 * `rest_create`, `kafka`, …) and user-defined kinds are likewise plain
 * identifiers.
 */

import type { Position, SourceSpan } from "./types.js";
import { renderDiagnostic } from "./diagnostics/catalog.js";

// =============================================================================
// Token Types
// =============================================================================

export type TokenType =
  // Reserved keywords
  | "TYPE"
  | "IN"
  | "LABELS"
  | "ASPECT"
  | "REQUIRED"
  | "CASCADE"
  | "APPEND"
  | "OVERRIDE"
  | "DROP"
  | "PROCESS"
  | "SUBPROCESS"
  | "VIEW"
  | "DECORATION"
  | "IF"
  | "ELSE"
  | "SELECT"
  | "ONE"
  | "EACH"
  | "TRY"
  | "CATCH"
  | "PARALLEL"
  | "BRANCH"
  | "JOIN"
  | "RACE"
  | "OUT"
  | "AWAIT"
  | "REVERSIBLE"
  | "UNWIND"
  | "ON"
  | "DO"
  | "GO"
  | "AS"
  | "DIST"
  | "FAIL"
  | "FINISH"
  | "FOCUS"
  | "GROUP"
  | "BY"
  | "LAYOUT"
  | "INCLUDE"
  | "EXCLUDE"
  | "USE"
  | "FROM"
  | "EXPORT"
  // Literals
  | "IDENTIFIER"
  | "STABLE_ID"
  | "STRING"
  | "NUMBER"
  | "TRUE"
  | "FALSE"
  // Punctuation
  | "LBRACE"
  | "RBRACE"
  | "LPAREN"
  | "RPAREN"
  | "LBRACKET"
  | "RBRACKET"
  | "COLON"
  | "SEMICOLON"
  | "COMMA"
  | "DOT"
  | "GT"
  | "EQUALS"
  | "STAR"
  | "PIPE"
  | "BACKSLASH"
  // v0.10 selector / expression operators
  | "AT" // `@`  — field / builtin getter sigil
  | "ATAT" // `@@` — aspect sigil
  | "DOLLAR" // `$`  — knob sigil
  | "GTGT" // `>>` — upstream/downstream cone
  | "LTGT" // `<>` — undirected edge / (expr) "use !="
  | "LT" // `<`
  | "LE" // `<=`
  | "GE" // `>=`
  | "NE" // `!=`
  | "PLUS" // `+`
  | "MINUS" // `-`  — binary/unary minus (numbers are unsigned; sign is a token)
  | "SLASH" // `/`  — division (comments are consumed as trivia)
  // Special
  | "EOF"
  | "ERROR";

export type TriviaKind = "whitespace" | "lineComment" | "blockComment";

export interface Trivia {
  readonly kind: TriviaKind;
  readonly text: string;
  readonly span: SourceSpan;
}

export interface Token {
  readonly type: TokenType;
  readonly value: string;
  readonly span: SourceSpan;
  readonly leadingTrivia: readonly Trivia[];
  readonly trailingTrivia: readonly Trivia[];
}

interface MutableToken {
  type: TokenType;
  value: string;
  span: SourceSpan;
  leadingTrivia: Trivia[];
  trailingTrivia: Trivia[];
}

// =============================================================================
// Keywords
// =============================================================================

// UAX #31 identifier character classes (v0.8.2). Precompiled; tested per code
// unit (BMP scripts — Cyrillic, common CJK — are single UTF-16 units; astral
// identifier chars are a known limitation of the code-unit scanner).
const RE_ID_START = /\p{ID_Start}/u; //  excludes `_` (Pc) — so no leading underscore
const RE_ID_CONTINUE = /\p{ID_Continue}/u; // includes `_`, digits, combining marks
const RE_LETTER = /\p{L}/u;

const KEYWORDS: Record<string, TokenType> = {
  type: "TYPE",
  in: "IN",
  aspects: "LABELS", // v0.9.0: reserved-with-error one cycle — renamed to `aspect`
  aspect: "ASPECT",
  required: "REQUIRED",
  cascade: "CASCADE",
  append: "APPEND",
  override: "OVERRIDE",
  drop: "DROP",
  process: "PROCESS",
  subprocess: "SUBPROCESS",
  view: "VIEW",
  if: "IF",
  else: "ELSE",
  select: "SELECT",
  one: "ONE",
  each: "EACH",
  try: "TRY",
  catch: "CATCH",
  parallel: "PARALLEL",
  branch: "BRANCH",
  join: "JOIN",
  race: "RACE",
  out: "OUT",
  await: "AWAIT",
  reversible: "REVERSIBLE",
  unwind: "UNWIND",
  on: "ON",
  do: "DO",
  go: "GO",
  as: "AS",
  dist: "DIST",
  fail: "FAIL",
  finish: "FINISH",
  focus: "FOCUS",
  group: "GROUP",
  by: "BY",
  layout: "LAYOUT",
  include: "INCLUDE",
  exclude: "EXCLUDE",
  true: "TRUE",
  false: "FALSE",
  use: "USE",
  from: "FROM",
  export: "EXPORT",
  // Reserved, not contextual: `decoration Name {` needs an unambiguous head and
  // has no `headIsField()` escape that would keep it usable as a field name.
  decoration: "DECORATION",
};

// =============================================================================
// Lexer
// =============================================================================

export interface LexerError {
  /** English rendering — always present, the fallback when `msgKey` is unset. */
  readonly message: string;
  readonly span: SourceSpan;
  /** Catalog key + params so lex errors render per-locale like parse/semantic
   *  diagnostics (see diagnostics/catalog.ts). `code` is always "PARSE_ERROR". */
  readonly code?: string;
  readonly msgKey?: string;
  readonly params?: Readonly<Record<string, unknown>>;
}

export interface LexResult {
  readonly tokens: readonly Token[];
  readonly errors: readonly LexerError[];
}

export class Lexer {
  private readonly source: string;
  private readonly file?: string;
  private pos: number = 0;
  private line: number = 1;
  private column: number = 1;
  private tokens: MutableToken[] = [];
  private errors: LexerError[] = [];
  private pendingLeading: Trivia[] = [];

  constructor(source: string, file?: string) {
    this.source = source;
    this.file = file;
  }

  lex(): LexResult {
    this.pendingLeading = this.consumeTrivia(false);

    while (!this.isAtEnd()) {
      this.scanToken();
      const trailing = this.consumeTrivia(true);
      const last = this.tokens[this.tokens.length - 1];
      if (last) last.trailingTrivia = trailing;
      this.pendingLeading = this.consumeTrivia(false);
    }

    this.addToken("EOF", "");
    return { tokens: this.tokens, errors: this.errors };
  }

  private scanToken(): void {
    const start = this.position();
    const ch = this.advance();

    switch (ch) {
      case "{":
        this.addToken("LBRACE", ch);
        break;
      case "}":
        this.addToken("RBRACE", ch);
        break;
      case "(":
        this.addToken("LPAREN", ch);
        break;
      case ")":
        this.addToken("RPAREN", ch);
        break;
      case "[":
        this.addToken("LBRACKET", ch);
        break;
      case "]":
        this.addToken("RBRACKET", ch);
        break;
      case ":":
        this.addToken("COLON", ch);
        break;
      case ";":
        this.addToken("SEMICOLON", ch);
        break;
      case "|":
        this.addToken("PIPE", ch);
        break;
      case "\\":
        this.addToken("BACKSLASH", ch);
        break;
      case ",":
        this.addToken("COMMA", ch);
        break;
      case ".":
        this.addToken("DOT", ch);
        break;
      case "*":
        this.addToken("STAR", ch);
        break;
      case "+":
        this.addToken("PLUS", ch);
        break;
      case "/":
        // Comments (`//`, `/*`) are consumed as trivia before scanToken runs,
        // so a `/` reaching here is the division operator (v0.10 expressions).
        this.addToken("SLASH", ch);
        break;
      case "-":
        // v0.10: `-` is uniformly a MINUS token. Numbers are unsigned; the sign
        // is a separate token resolved by the parser (unary minus in
        // expressions, signed literal in value slots). Glued `foo-bar` hyphens
        // still join inside `identifier()` — they never reach here.
        this.addToken("MINUS", ch);
        break;
      case "=":
        this.addToken("EQUALS", ch);
        break;
      case "!":
        if (this.match("=")) {
          this.addTokenFromStart(start, "NE", "!=");
        } else {
          this.error(start, "parse.unexpectedChar", { ch });
        }
        break;
      case "<":
        if (this.match("=")) {
          this.addTokenFromStart(start, "LE", "<=");
        } else if (this.match(">")) {
          this.addTokenFromStart(start, "LTGT", "<>");
        } else {
          this.addToken("LT", ch);
        }
        break;
      case ">":
        if (this.match("=")) {
          this.addTokenFromStart(start, "GE", ">=");
        } else if (this.match(">")) {
          this.addTokenFromStart(start, "GTGT", ">>");
        } else {
          this.addToken("GT", ch);
        }
        break;
      case "@":
        if (this.match("@")) {
          this.addTokenFromStart(start, "ATAT", "@@");
        } else {
          this.addToken("AT", ch);
        }
        break;
      case "$":
        this.addToken("DOLLAR", ch);
        break;
      case "#":
        this.stableId(start);
        break;
      case '"':
        if (this.peek() === '"' && this.peekNext() === '"') {
          this.advance(); // second "
          this.advance(); // third "
          this.tripleString(start);
        } else {
          this.string(start);
        }
        break;
      default:
        if (this.isDigit(ch)) {
          // v0.8.2: a digit-led run that carries a letter is an identifier
          // (`2FA`, `3D`); a pure-digit run is a number.
          if (this.digitRunHasLetter()) {
            this.identifier(start);
          } else {
            this.number(start);
          }
        } else if (this.isIdentStart(ch)) {
          this.identifier(start);
        } else {
          this.error(start, "parse.unexpectedChar", { ch });
        }
    }
  }

  /**
   * Consume whitespace and comments as trivia.
   *
   * When `stopAtNewline` is true (trailing-trivia mode), collection halts
   * after the first newline is included, or after a block comment that
   * internally spanned a newline. Otherwise (leading-trivia mode), keep
   * collecting until a real token starts or EOF.
   */
  private consumeTrivia(stopAtNewline: boolean): Trivia[] {
    const items: Trivia[] = [];

    while (!this.isAtEnd()) {
      const ch = this.peek();

      if (ch === " " || ch === "\t" || ch === "\r" || ch === "\n") {
        const start = this.position();
        let seenNewline = false;
        while (!this.isAtEnd()) {
          const c = this.peek();
          if (c === "\n") {
            this.advance();
            this.line++;
            this.column = 1;
            seenNewline = true;
            if (stopAtNewline) break;
          } else if (c === " " || c === "\t" || c === "\r") {
            this.advance();
          } else {
            break;
          }
        }
        const end = this.position();
        items.push({
          kind: "whitespace",
          text: this.source.slice(start.offset, end.offset),
          span: { start, end, file: this.file },
        });
        if (seenNewline && stopAtNewline) break;
        continue;
      }

      if (ch === "/" && this.peekNext() === "/") {
        const start = this.position();
        while (!this.isAtEnd() && this.peek() !== "\n") {
          this.advance();
        }
        const end = this.position();
        items.push({
          kind: "lineComment",
          text: this.source.slice(start.offset, end.offset),
          span: { start, end, file: this.file },
        });
        continue;
      }

      if (ch === "/" && this.peekNext() === "*") {
        const start = this.position();
        this.advance(); // /
        this.advance(); // *
        let hadNewline = false;
        while (!this.isAtEnd()) {
          if (this.peek() === "*" && this.peekNext() === "/") {
            this.advance(); // *
            this.advance(); // /
            break;
          }
          if (this.peek() === "\n") {
            hadNewline = true;
            this.line++;
            this.column = 0;
          }
          this.advance();
        }
        const end = this.position();
        items.push({
          kind: "blockComment",
          text: this.source.slice(start.offset, end.offset),
          span: { start, end, file: this.file },
        });
        if (stopAtNewline && hadNewline) break;
        continue;
      }

      break;
    }

    return items;
  }

  private identifier(start: Position): void {
    while (this.isIdentContinue(this.peek())) {
      this.advance();
    }
    // Allow internal hyphens that join two runs, e.g. `arch-microservice`,
    // `kebab-case-name`. A trailing `-` is left outside the identifier so
    // `foo > bar` still tokenises cleanly, and `-3` still lexes as a number.
    while (this.peek() === "-" && this.isIdentContinue(this.peekNext())) {
      this.advance(); // consume the '-'
      while (this.isIdentContinue(this.peek())) {
        this.advance();
      }
    }

    // v0.8.2: identities key on the NFC-normalized form.
    const value = this.source.slice(start.offset, this.pos).normalize("NFC");
    const type = KEYWORDS[value] ?? "IDENTIFIER";
    this.addTokenFromStart(start, type, value);
  }

  /**
   * From the current position (just past a leading digit), does the maximal
   * run of identifier-continue characters contain a letter? If so the token is
   * an identifier (`2FA`, `3D`), not a number.
   */
  private digitRunHasLetter(): boolean {
    let i = 0;
    for (;;) {
      const c = this.peekAt(i);
      if (!this.isIdentContinue(c)) break;
      if (this.isLetter(c)) return true;
      i++;
    }
    return false;
  }

  private stableId(start: Position): void {
    while (this.isAlphaNumeric(this.peek())) {
      this.advance();
    }

    const value = this.source.slice(start.offset, this.pos);
    if (value.length === 1) {
      // Lone '#' with no suffix
      this.error(start, "parse.stableIdSuffix");
      return;
    }
    this.addTokenFromStart(start, "STABLE_ID", value);
  }

  private string(start: Position): void {
    while (!this.isAtEnd() && this.peek() !== '"') {
      if (this.peek() === "\n") {
        this.line++;
        this.column = 0;
      }
      if (this.peek() === "\\" && (this.peekNext() === '"' || this.peekNext() === "\\")) {
        // A backslash escapes the next `"` OR `\` — consume the pair as a unit
        // so a `\\` right before the closing quote can't swallow it.
        this.advance(); // skip backslash
      }
      this.advance();
    }

    if (this.isAtEnd()) {
      this.error(start, "parse.unterminatedString");
      return;
    }

    this.advance(); // closing "

    const raw = this.source.slice(start.offset + 1, this.pos - 1);
    const unescaped = raw.replace(/\\(["\\])/g, "$1");
    const value = normalizeMultilineString(unescaped);
    this.addTokenFromStart(start, "STRING", value);
  }

  /**
   * Triple-quoted string. Content between """ and """ is taken verbatim
   * — no escape sequences, so single AND double quotes can appear
   * unescaped inside (as long as a `"` run doesn't reach three). The
   * value is then trimmed + dedented by the SAME `normalizeMultilineString`
   * pass the regular `"…"` form uses, so an indented triple-quoted block
   * reads as flush-left text. The only difference from `"…"` is the
   * delimiter and the lack of escaping.
   */
  private tripleString(start: Position): void {
    while (!this.isAtEnd()) {
      if (this.peek() === '"' && this.peekNext() === '"' && this.peekAt(2) === '"') {
        // closing """
        const raw = this.source.slice(start.offset + 3, this.pos);
        this.advance(); this.advance(); this.advance();
        this.addTokenFromStart(start, "STRING", normalizeMultilineString(raw));
        return;
      }
      if (this.peek() === "\n") {
        this.line++;
        this.column = 0;
      }
      this.advance();
    }
    this.error(start, "parse.unterminatedTripleString");
  }

  private number(start: Position): void {
    while (this.isDigit(this.peek())) {
      this.advance();
    }

    if (this.peek() === "." && this.isDigit(this.peekNext())) {
      this.advance(); // consume .
      while (this.isDigit(this.peek())) {
        this.advance();
      }
    }

    const value = this.source.slice(start.offset, this.pos);
    this.addTokenFromStart(start, "NUMBER", value);
  }

  // =========================================================================
  // Helpers
  // =========================================================================

  private isAtEnd(): boolean {
    return this.pos >= this.source.length;
  }

  private peek(): string {
    if (this.isAtEnd()) return "\0";
    return this.source[this.pos]!;
  }

  private peekNext(): string {
    if (this.pos + 1 >= this.source.length) return "\0";
    return this.source[this.pos + 1]!;
  }

  private peekAt(offset: number): string {
    return this.pos + offset >= this.source.length
      ? "\0"
      : this.source[this.pos + offset]!;
  }

  private advance(): string {
    const ch = this.source[this.pos]!;
    this.pos++;
    this.column++;
    return ch;
  }

  private match(expected: string): boolean {
    if (this.isAtEnd()) return false;
    if (this.source[this.pos] !== expected) return false;
    this.pos++;
    this.column++;
    return true;
  }

  private isDigit(ch: string): boolean {
    return ch >= "0" && ch <= "9";
  }

  private isAlpha(ch: string): boolean {
    return (ch >= "a" && ch <= "z") || (ch >= "A" && ch <= "Z") || ch === "_";
  }

  private isAlphaNumeric(ch: string): boolean {
    return this.isAlpha(ch) || this.isDigit(ch);
  }

  // v0.8.2 UAX #31 classes. `\0` sentinel and other non-letters test false.
  private isIdentStart(ch: string): boolean {
    return RE_ID_START.test(ch);
  }

  private isIdentContinue(ch: string): boolean {
    return RE_ID_CONTINUE.test(ch);
  }

  private isLetter(ch: string): boolean {
    return RE_LETTER.test(ch);
  }

  private position(): Position {
    return { line: this.line, column: this.column, offset: this.pos };
  }

  private addToken(type: TokenType, value: string): void {
    const end = this.position();
    const start: Position = {
      line: end.line,
      column: end.column - value.length,
      offset: end.offset - value.length,
    };
    this.pushToken(type, value, { start, end, file: this.file });
  }

  private addTokenFromStart(start: Position, type: TokenType, value: string): void {
    const end = this.position();
    this.pushToken(type, value, { start, end, file: this.file });
  }

  private pushToken(type: TokenType, value: string, span: SourceSpan): void {
    this.tokens.push({
      type,
      value,
      span,
      leadingTrivia: this.pendingLeading,
      trailingTrivia: [],
    });
    this.pendingLeading = [];
  }

  private error(start: Position, msgKey: string, params?: Record<string, unknown>): void {
    const end = this.position();
    const message = renderDiagnostic(msgKey, params, "en") ?? msgKey;
    this.errors.push({
      message,
      span: { start, end, file: this.file },
      code: "PARSE_ERROR",
      msgKey,
      ...(params ? { params } : {}),
    });
    this.addTokenFromStart(start, "ERROR", this.source.slice(start.offset, this.pos));
  }
}

/**
 * Normalize multi-line string literals (trim + dedent).
 * Single-line strings are returned unchanged.
 */
function normalizeMultilineString(value: string): string {
  if (!value.includes("\n")) return value;

  const lines = value.split("\n");

  if (lines.length > 0 && lines[0]!.trim() === "") {
    lines.shift();
  }
  if (lines.length > 0 && lines[lines.length - 1]!.trim() === "") {
    lines.pop();
  }

  if (lines.length === 0) return "";

  let minIndent = Infinity;
  for (const line of lines) {
    if (line.trim() === "") continue;
    const match = line.match(/^[ \t]*/);
    const indent = match ? match[0].length : 0;
    if (indent < minIndent) minIndent = indent;
  }

  if (!Number.isFinite(minIndent) || minIndent === 0) {
    return lines.join("\n");
  }

  return lines.map((line) => line.slice(minIndent)).join("\n");
}

/**
 * Tokenize ArchLang source code
 */
export function tokenize(source: string, file?: string): LexResult {
  return new Lexer(source, file).lex();
}

/**
 * The set of all v0.2 reserved keywords (lowercase). Useful for the parser
 * and code-action providers to validate user-defined identifiers do not
 * collide with reserved tokens.
 */
export const RESERVED_KEYWORDS: ReadonlySet<string> = new Set(Object.keys(KEYWORDS));
